import { API_HOST } from '~/constants';
import { LocaleEnum } from '~/locales/resources';

export const API_ENDPOINT_NAMES = {
  quote: 'quote',
  order: 'order',
  fulfillmentEvent: 'fulfillment_event',
} as const;

export const ENDPOINT = {
  me: '/me',
  preferences: '/me/preferences',
  quotes: '/quotes',
  quotesRequest: '/quote-requests/v2',
  invoices: '/customer-invoices',
  recommendedProducts: '/inventory/products/recommended',
  fulfillmentEvents: '/fulfillment-events',
  owners: '/owners',
  orders: '/orders',
  search: '/search', // endpoint to search for offers(quotes)/orders/deliveries(fullfilment-events)
  inventory: '/inventory',
  claims: '/v2/claims',
  notifications: '/notifications',
  auth: '/auth',
  authLogin: '/auth/login',
  authAdminLogin: '/auth/admin-login',
  updatePreferences: '/me/preferences',
  updateDeliveryAddress: '/me/update-delivery-address',
  updateDeliveryContact: '/me/update-delivery-contact',
  updateDeliveryInfo: '/me/update-delivery-info',
  updatePassword: '/auth/update-password',
  updateBillingAddress: '/me/update-billing-address',
  documents: '/documents',
  // PDP page endpoints
  shopProductDetails: '/shop/inventory/products', // Get request to get product details on PDP page
  pdpWeeklyPriceList: '/inventory/products/price/pq-weeks', // Get request to get weekly price list on PDP page
  cartItem: '/v2/cart/item', // addTocart PDP page, updateCartItem or deleteCartItem on cart page
  // Cart endpoints
  cart: '/v2/cart', // Get request to get cart items on cart page or patch request to update deliver address
  checkout: '/v2/cart/checkout', // POST & GET request on cart page to create quote and order. GET request to get checkout page details
  cost: '/v2/cart/cost', // POST request on cart page to send total cost to checkout page
  cartDeliveryCost: '/v2/cart/delivery-cost', // PATCH request on cart page to update delivery cost
  // Checkout page endpoints
  checkoutPlaceOrder: '/v2/cart/place-order', // POST request to place order
  checkoutBnplOptions: '/v2/cart/bnpl/options', // GET request to get BNPL options
  // Success page
  cartSuccess: '/shop/cart/success', // Get request to get cart items on success page
  validateAdrress: '/address/validate',
  priceEngine: '/inventory/products/price/quote', // POST request to get list of cart items (Cart page)
  // Cart shipping address
  shippingAddress: '/shop/cart/delivery-info',
  // Price engine
  priceEngineDynamic: '/shop/inventory/products/price/dynamic', // POST request to get the dynamic price (PDP page)
  categoryFilters: '/shop/inventory/filters',
  // Leads create endpoint
  leadsCreate: '/lead',
} as const;

type EndpointKeys = keyof typeof ENDPOINT;

/**
 * API endpoints with full URLs
 */
export const API_ENDPOINT = {
  me: `${API_HOST}${ENDPOINT.me}`,
  preferences: `${API_HOST}${ENDPOINT.preferences}`,
  quotes: `${API_HOST}${ENDPOINT.quotes}`,
  quotesRequest: `${API_HOST}${ENDPOINT.quotesRequest}`,
  invoices: `${API_HOST}${ENDPOINT.invoices}`,
  recommendedProducts: `${API_HOST}${ENDPOINT.recommendedProducts}`,
  fulfillmentEvents: `${API_HOST}${ENDPOINT.fulfillmentEvents}`,
  owners: `${API_HOST}${ENDPOINT.owners}`,
  orders: `${API_HOST}${ENDPOINT.orders}`,
  search: `${API_HOST}${ENDPOINT.search}`,
  inventory: `${API_HOST}${ENDPOINT.inventory}`,
  claims: `${API_HOST}${ENDPOINT.claims}`,
  notifications: `${API_HOST}${ENDPOINT.notifications}`,
  auth: `${API_HOST}${ENDPOINT.auth}`,
  authLogin: `${API_HOST}${ENDPOINT.authLogin}`,
  authAdminLogin: `${API_HOST}${ENDPOINT.authAdminLogin}`,
  updatePreferences: `${API_HOST}${ENDPOINT.updatePreferences}`,
  updateDeliveryAddress: `${API_HOST}${ENDPOINT.updateDeliveryAddress}`,
  updateDeliveryContact: `${API_HOST}${ENDPOINT.updateDeliveryContact}`,
  updateDeliveryInfo: `${API_HOST}${ENDPOINT.updateDeliveryInfo}`,
  updatePassword: `${API_HOST}${ENDPOINT.updatePassword}`,
  updateBillingAddress: `${API_HOST}${ENDPOINT.updateBillingAddress}`,
  documents: `${API_HOST}${ENDPOINT.documents}`,
  // Shop endpoints - Used in PDP page
  shopProductDetails: `${API_HOST}${ENDPOINT.shopProductDetails}`,
  // PDP page endpoints
  pdpWeeklyPriceList: `${API_HOST}${ENDPOINT.pdpWeeklyPriceList}`,
  cart: `${API_HOST}${ENDPOINT.cart}`,
  cartSuccess: `${API_HOST}${ENDPOINT.cartSuccess}`,
  cartItem: `${API_HOST}${ENDPOINT.cartItem}`,
  checkout: `${API_HOST}${ENDPOINT.checkout}`, // POST request to create quote and order and get checkout page details
  cost: `${API_HOST}${ENDPOINT.cost}`,
  cartDeliveryCost: `${API_HOST}${ENDPOINT.cartDeliveryCost}`,
  checkoutPlaceOrder: `${API_HOST}${ENDPOINT.checkoutPlaceOrder}`,
  checkoutBnplOptions: `${API_HOST}${ENDPOINT.checkoutBnplOptions}`,
  validateAdrress: `${API_HOST}${ENDPOINT.validateAdrress}`,
  priceEngine: `${API_HOST}${ENDPOINT.priceEngine}`,
  shippingAddress: `${API_HOST}${ENDPOINT.cart}`,
  priceEngineDynamic: `${API_HOST}${ENDPOINT.priceEngineDynamic}`,
  categoryFilters: `${API_HOST}${ENDPOINT.categoryFilters}`,
  leadsCreate: `${API_HOST}${ENDPOINT.leadsCreate}`,
} as const;

export const getOfferPdfEndpoint = (id: string) => `${API_ENDPOINT.quotes}/${id}/offer-pdf`;

export const getOfferDetailsEndpoint = (id: string) => `${API_ENDPOINT.quotes}/${id}/offer-details`;

export const getBillingDocumentLink = (id: string) => `/api/home/billing/${id}/documents`;

/**
 * BNPL (Buy Now, Pay Later) endpoints. The same shape (`/options` and `/apply`)
 * is exposed for three resources: quotes, orders and fulfillment-events. The
 * helpers below build the absolute URL for each combination so callers don't
 * have to repeat the host/prefix.
 */
export const getBnplOptionsForQuoteEndpoint = (quoteId: string) =>
  `${API_HOST}/v2/quotes/${quoteId}/bnpl/options`;
export const getBnplApplyForQuoteEndpoint = (quoteId: string) =>
  `${API_HOST}/v2/quotes/${quoteId}/bnpl/apply`;
export const getBnplOptionsForOrderEndpoint = (orderId: string) =>
  `${API_HOST}/v2/orders/${orderId}/bnpl/options`;
export const getBnplApplyForOrderEndpoint = (orderId: string) =>
  `${API_HOST}/v2/orders/${orderId}/bnpl/apply`;
export const getBnplOptionsForFulfillmentEventEndpoint = (fulfillmentEventId: string) =>
  `${API_HOST}/v2/fulfillment-events/${fulfillmentEventId}/bnpl/options`;
export const getBnplApplyForFulfillmentEventEndpoint = (fulfillmentEventId: string) =>
  `${API_HOST}/v2/fulfillment-events/${fulfillmentEventId}/bnpl/apply`;

export const AndercoreExternalLinks: Record<
  LocaleEnum,
  {
    privacy: string;
    imprint: string;
    terms: string;
    buyerLogin: string;
    supplierLogin: string;
    andercoreHome: string;
    faqs: string;
    linkedIn: string;
  }
> = {
  de: {
    privacy: 'https://andercore.com/de/datenschutz/',
    imprint: 'https://andercore.com/de/impressum/',
    terms: 'https://andercore.com/de/agb/',
    buyerLogin: 'https://buy.andercore.com/de/auth/login',
    supplierLogin: 'https://supplier.andercore.com/de/auth/login',
    andercoreHome: 'https://andercore.com/de',
    faqs: 'https://andercore.com/de/faqs/',
    linkedIn: 'https://www.linkedin.com/company/andercore-com/about/?viewAsMember=true',
  },
  en: {
    privacy: 'https://andercore.com/privacy',
    imprint: 'https://andercore.com/imprint/',
    terms: 'https://andercore.com/terms/',
    buyerLogin: 'https://buy.andercore.com/en/auth/login',
    supplierLogin: 'https://supplier.andercore.com/en/auth/login',
    andercoreHome: 'https://andercore.com/en',
    faqs: 'https://andercore.com/faqs/',
    linkedIn: 'https://www.linkedin.com/company/andercore-com/about/?viewAsMember=true',
  },
  fr: {
    privacy: 'https://andercore.com/fr/politique-de-confidentialite',
    imprint: 'https://andercore.com/fr/mentions-legales/',
    terms: 'https://andercore.com/fr/cgv/',
    buyerLogin: 'https://buy.andercore.com/fr/auth/login',
    supplierLogin: 'https://supplier.andercore.com/fr/auth/login',
    andercoreHome: 'https://andercore.com/fr',
    faqs: 'https://andercore.com/fr/faq/',
    linkedIn: 'https://www.linkedin.com/company/andercore-com/about/?viewAsMember=true',
  },
  it: {
    privacy: 'https://andercore.com/it/privacy',
    imprint: 'https://andercore.com/it/note-legali',
    terms: 'https://andercore.com/it/condizioni-generali',
    buyerLogin: 'https://buy.andercore.com/it/auth/login',
    supplierLogin: 'https://supplier.andercore.com/it/auth/login',
    andercoreHome: 'https://andercore.com/it',
    faqs: 'https://andercore.com/it/faqs/',
    linkedIn: 'https://www.linkedin.com/company/andercore-com/about/?viewAsMember=true',
  },
  es: {
    privacy: 'https://andercore.com/privacy',
    imprint: 'https://andercore.com/imprint/',
    terms: 'https://andercore.com/terms/',
    buyerLogin: 'https://buy.andercore.com/es/auth/login',
    supplierLogin: 'https://supplier.andercore.com/es/auth/login',
    andercoreHome: 'https://andercore.com/es',
    faqs: 'https://andercore.com/faqs/',
    linkedIn: 'https://www.linkedin.com/company/andercore-com/about/?viewAsMember=true',
  },
};
